import { useState, useMemo } from 'react';
import { BookOpen, Search, Play, X, Dumbbell } from 'lucide-react';
import { exerciseBankData } from '../../data/exerciseBankData';

export function ExerciseBankPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedExercise, setSelectedExercise] = useState(null);
  const [imageErrors, setImageErrors] = useState(new Set());

  // Filter and sort exercises alphabetically
  const filteredExercises = useMemo(() => {
    const lowerQuery = searchQuery.toLowerCase().trim();
    return exerciseBankData
      .filter((ex) => ex.name.toLowerCase().includes(lowerQuery))
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
  }, [searchQuery]);

  // Group exercises by first letter
  const groupedExercises = useMemo(() => {
    const groups = {};
    filteredExercises.forEach((exercise) => {
      const firstChar = exercise.name.charAt(0).toUpperCase();
      const key = /[A-Z]/.test(firstChar) ? firstChar : '#';
      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(exercise);
    });
    return groups;
  }, [filteredExercises]);

  const sortedLetters = Object.keys(groupedExercises).sort((a, b) => {
    if (a === '#') return -1;
    if (b === '#') return 1;
    return a.localeCompare(b);
  });

  const handleImageError = (exerciseId) => {
    setImageErrors((prev) => new Set(prev).add(exerciseId));
  };

  const openModal = (exercise) => {
    setSelectedExercise(exercise);
  };

  const closeModal = () => {
    setSelectedExercise(null);
  };

  return (
    <div className="max-w-2xl mx-auto p-4 pb-24 lg:pb-8">
      {/* Header */}
      <div className="flex items-center mb-6">
        <BookOpen className="h-6 w-6 text-primary mr-2" />
        <h1 className="text-2xl font-bold text-gray-900">Exercise Bank</h1>
      </div>

      {/* Search Bar */}
      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
        <input
          type="text"
          placeholder="Search exercises..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
        />
        {searchQuery && (
          <button
            onClick={() => setSearchQuery('')}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </div>

      {/* Exercise Count */}
      <p className="text-sm text-gray-500 mb-4">
        {filteredExercises.length} exercise{filteredExercises.length !== 1 ? 's' : ''} found
      </p>

      {/* Exercise List */}
      {filteredExercises.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
          <Dumbbell className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No exercises found matching "{searchQuery}"</p>
          <button
            onClick={() => setSearchQuery('')}
            className="mt-3 text-primary hover:text-primary/80 text-sm font-medium"
          >
            Clear search
          </button>
        </div>
      ) : (
        <div className="space-y-6">
          {sortedLetters.map((letter) => (
            <div key={letter}>
              {/* Letter Header */}
              <div className="sticky top-0 z-10 bg-gray-50 py-2 px-1 -mx-1 mb-2">
                <span className="text-sm font-semibold text-primary uppercase tracking-wide">
                  {letter}
                </span>
              </div>

              {/* Exercises for this letter */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden divide-y divide-gray-100">
                {groupedExercises[letter].map((exercise) => (
                  <button
                    key={exercise.id}
                    onClick={() => openModal(exercise)}
                    className="w-full flex items-center p-3 hover:bg-gray-50 transition-colors text-left group"
                  >
                    {/* Exercise Icon/Thumbnail */}
                    <div className="flex-shrink-0 w-14 h-14 rounded-lg overflow-hidden bg-gray-100 mr-3">
                      {imageErrors.has(exercise.id) ? (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/10 to-primary/5">
                          <Dumbbell className="h-6 w-6 text-primary/40" />
                        </div>
                      ) : (
                        <img
                          src={exercise.iconUrl}
                          alt={exercise.name}
                          className="w-full h-full object-cover"
                          onError={() => handleImageError(exercise.id)}
                          loading="lazy"
                        />
                      )}
                    </div>

                    {/* Exercise Name */}
                    <div className="flex-1 min-w-0">
                      <p className="text-gray-900 font-medium truncate group-hover:text-primary transition-colors">
                        {exercise.name}
                      </p>
                      <p className="text-xs text-gray-400 mt-0.5">Tap to watch video</p>
                    </div>

                    {/* Play Icon */}
                    <div className="flex-shrink-0 ml-2">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary group-hover:text-white transition-all">
                        <Play className="h-4 w-4 text-primary group-hover:text-white ml-0.5" />
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Video Modal */}
      {selectedExercise && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          onClick={closeModal}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900 truncate pr-4">
                {selectedExercise.name}
              </h2>
              <button
                onClick={closeModal}
                className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>

            {/* Video Container */}
            <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
              <iframe
                className="absolute inset-0 w-full h-full"
                src={`${selectedExercise.videoEmbedUrl}?autoplay=1&rel=0`}
                title={selectedExercise.name}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>

            {/* Modal Footer - removing for now so don't go to youtube
            <div className="p-4 bg-gray-50 border-t border-gray-100">
              <a
                href={selectedExercise.videoUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-primary hover:text-primary/80 font-medium"
              >
                Open in YouTube →
              </a>
            </div>
            */}
          </div>
        </div>
      )}
    </div>
  );
}